import React, { useState, useEffect, useRef } from 'react';
import {
  Tag,
  Percent,
  ShoppingBag,
  Zap,
  Gift,
  Star,
  ChevronRight,
  ChevronDown,
  Menu,
  X,
  Globe,
  Smartphone,
  Search,
  Clock,
  TrendingUp,
  ShieldCheck
} from 'lucide-react';
import ReactCountryFlag from 'react-country-flag';

const LANGUAGES = [
  { code: 'de', name: 'Deutsch', flag: 'DE' },
  { code: 'fr', name: 'Français', flag: 'FR' },
  { code: 'lb', name: 'Lëtzebuergesch', flag: 'LU' },
  { code: 'en', name: 'English', flag: 'GB' },
];

const content = {
  de: {
    nav: {
      deals: "Angebote",
      howItWorks: "So geht's",
      categories: "Kategorien",
      faq: "FAQ",
    },
    hero: {
      badge: "Neu in Luxemburg",
      title: "Spare bis zu 70% mit exklusiven Gutscheincodes",
      subtitle: "DealShoppy24 bringt dir täglich die besten Rabattcodes und Sonderangebote direkt auf dein Handy. Mode, Elektronik, Reisen und mehr — alles an einem Ort.",
      cta: "Deals entdecken",
      trust: "Bereits Tausende Nutzer in Luxemburg",
    },
    deals: {
      title: "Heutige Top-Deals",
      subtitle: "Jeden Tag neue Gutscheincodes — handverlesen und sofort einsetzbar",
      categories: ["Mode & Accessoires", "Elektronik & Technik", "Essen & Trinken", "Reisen & Freizeit", "Haus & Garten", "Beauty & Pflege"],
      discounts: ["Bis zu 50% Rabatt", "Bis zu 40% Rabatt", "Bis zu 30% Rabatt", "Bis zu 60% Rabatt", "Bis zu 35% Rabatt", "Bis zu 45% Rabatt"],
      cta: "Code anfordern",
    },
    howItWorks: {
      title: "In 3 Schritten zum besten Deal",
      subtitle: "So einfach sparst du mit DealShoppy24",
      steps: [
        { title: "Stöbern & Entdecken", description: "Durchsuche unsere ständig aktualisierte Sammlung an Gutscheincodes aus allen Kategorien." },
        { title: "Code erhalten", description: "Fordere deinen persönlichen Rabattcode an — direkt per SMS auf dein Handy." },
        { title: "Einlösen & Sparen", description: "Verwende den Code beim Online-Shop deiner Wahl und genieße sofortige Ersparnisse." },
      ],
    },
    benefits: {
      title: "Warum DealShoppy24?",
      subtitle: "Smarter einkaufen war noch nie so einfach",
      items: [
        { title: "Täglich neue Codes", description: "Unser Team sucht rund um die Uhr nach den besten Angeboten für dich." },
        { title: "100% verifiziert", description: "Jeder Gutscheincode wird vor der Veröffentlichung geprüft und getestet." },
        { title: "Sofortige Lieferung", description: "Erhalte deinen Rabattcode innerhalb von Sekunden direkt auf dein Smartphone." },
        { title: "Alle Top-Marken", description: "Von lokalen Luxemburger Shops bis zu internationalen Markenriesen." },
      ],
    },
    popular: {
      title: "Beliebte Kategorien",
      subtitle: "Entdecke Rabatte in den gefragtesten Bereichen",
    },
    faq: {
      title: "Häufig gestellte Fragen",
      items: [
        { q: "Wie erhalte ich meinen Gutscheincode?", a: "Nachdem du dich registriert hast, senden wir dir die Gutscheincodes direkt per SMS auf dein Mobiltelefon. So hast du deine Rabatte immer griffbereit." },
        { q: "Sind die Gutscheincodes wirklich kostenlos?", a: "Die Gutscheincodes selbst bieten dir Zugang zu exklusiven Rabatten bei Partnergeschäften. Für den DealShoppy24-Service gelten die in den AGB beschriebenen Konditionen." },
        { q: "Für welche Geschäfte gelten die Codes?", a: "Unsere Codes gelten bei einer Vielzahl von Online-Shops und lokalen Geschäften in Luxemburg und Europa — von Mode über Elektronik bis hin zu Reisen." },
        { q: "Wie oft werden neue Codes veröffentlicht?", a: "Täglich! Unser Team arbeitet rund um die Uhr, um die neuesten und besten Angebote für dich zu finden." },
        { q: "Kann ich den Service jederzeit kündigen?", a: "Ja, selbstverständlich. Du kannst den Service jederzeit per SMS kündigen. Sende einfach STOPDEAL an 67819." },
      ],
    },
    cta: {
      title: "Bereit zum Sparen?",
      subtitle: "Verpasse nie wieder einen Deal. Hol dir jetzt deine exklusiven Gutscheincodes.",
      button: "Jetzt starten",
    },
    footer: {
      privacy: "Datenschutz",
      terms: "AGB",
      contact: "Kontakt",
    },
  },
  fr: {
    nav: {
      deals: "Offres",
      howItWorks: "Comment ça marche",
      categories: "Catégories",
      faq: "FAQ",
    },
    hero: {
      badge: "Nouveau au Luxembourg",
      title: "Économisez jusqu'à 70% avec des codes promo exclusifs",
      subtitle: "DealShoppy24 vous livre chaque jour les meilleurs codes de réduction et offres spéciales directement sur votre téléphone. Mode, électronique, voyages et plus — tout en un seul endroit.",
      cta: "Découvrir les offres",
      trust: "Déjà des milliers d'utilisateurs au Luxembourg",
    },
    deals: {
      title: "Top deals du jour",
      subtitle: "Chaque jour de nouveaux codes promo — sélectionnés à la main et utilisables immédiatement",
      categories: ["Mode & Accessoires", "Électronique & Tech", "Alimentation & Boissons", "Voyages & Loisirs", "Maison & Jardin", "Beauté & Soins"],
      discounts: ["Jusqu'à 50% de réduction", "Jusqu'à 40% de réduction", "Jusqu'à 30% de réduction", "Jusqu'à 60% de réduction", "Jusqu'à 35% de réduction", "Jusqu'à 45% de réduction"],
      cta: "Obtenir le code",
    },
    howItWorks: {
      title: "Le meilleur deal en 3 étapes",
      subtitle: "Économiser avec DealShoppy24, c'est simple",
      steps: [
        { title: "Parcourir & Découvrir", description: "Explorez notre collection constamment mise à jour de codes promo dans toutes les catégories." },
        { title: "Recevoir le code", description: "Demandez votre code de réduction personnel — envoyé directement par SMS sur votre téléphone." },
        { title: "Utiliser & Économiser", description: "Utilisez le code dans la boutique en ligne de votre choix et profitez d'économies immédiates." },
      ],
    },
    benefits: {
      title: "Pourquoi DealShoppy24 ?",
      subtitle: "Faire des achats malins n'a jamais été aussi facile",
      items: [
        { title: "Nouveaux codes chaque jour", description: "Notre équipe recherche les meilleures offres pour vous 24h/24." },
        { title: "100% vérifiés", description: "Chaque code promo est vérifié et testé avant publication." },
        { title: "Livraison instantanée", description: "Recevez votre code de réduction en quelques secondes sur votre smartphone." },
        { title: "Toutes les grandes marques", description: "Des boutiques luxembourgeoises locales aux grandes marques internationales." },
      ],
    },
    popular: {
      title: "Catégories populaires",
      subtitle: "Découvrez des réductions dans les domaines les plus demandés",
    },
    faq: {
      title: "Questions fréquemment posées",
      items: [
        { q: "Comment recevoir mon code promo ?", a: "Après votre inscription, nous vous envoyons les codes promo directement par SMS sur votre téléphone portable. Vos réductions sont toujours à portée de main." },
        { q: "Les codes promo sont-ils vraiment gratuits ?", a: "Les codes promo vous donnent accès à des réductions exclusives chez nos partenaires. Les conditions du service DealShoppy24 sont décrites dans les CGV." },
        { q: "Pour quels magasins les codes fonctionnent-ils ?", a: "Nos codes fonctionnent dans de nombreuses boutiques en ligne et magasins locaux au Luxembourg et en Europe — de la mode à l'électronique en passant par les voyages." },
        { q: "À quelle fréquence de nouveaux codes sont-ils publiés ?", a: "Tous les jours ! Notre équipe travaille en permanence pour trouver les offres les plus récentes et les meilleures pour vous." },
        { q: "Puis-je annuler le service à tout moment ?", a: "Oui, bien sûr. Vous pouvez annuler le service à tout moment par SMS. Envoyez simplement STOPDEAL au 67819." },
      ],
    },
    cta: {
      title: "Prêt à économiser ?",
      subtitle: "Ne manquez plus jamais une bonne affaire. Obtenez vos codes promo exclusifs maintenant.",
      button: "Commencer maintenant",
    },
    footer: {
      privacy: "Confidentialité",
      terms: "Conditions",
      contact: "Contact",
    },
  },
  lb: {
    nav: {
      deals: "Offeren",
      howItWorks: "Wéi et fonctionnéiert",
      categories: "Kategorien",
      faq: "FAQ",
    },
    hero: {
      badge: "Nei zu Lëtzebuerg",
      title: "Spuer bis zu 70% mat exklusiven Aktiounscoden",
      subtitle: "DealShoppy24 bréngt dir all Dag déi bescht Rabattcoden an Sonderofferen direkt op däin Handy. Kleedung, Elektronik, Reesen a méi — alles op enger Plaz.",
      cta: "Offeren entdecken",
      trust: "Schonns Dausende Benotzer zu Lëtzebuerg",
    },
    deals: {
      title: "Top-Offere vun haut",
      subtitle: "All Dag nei Aktiounscoden — mat Hand erausgesicht an direkt abléisbar",
      categories: ["Kleedung & Accessoiren", "Elektronik & Technik", "Iessen & Drénken", "Reesen & Fräizäit", "Haus & Gaart", "Schéinheet & Fleeg"],
      discounts: ["Bis zu 50% Remise", "Bis zu 40% Remise", "Bis zu 30% Remise", "Bis zu 60% Remise", "Bis zu 35% Remise", "Bis zu 45% Remise"],
      cta: "Code ufroen",
    },
    howItWorks: {
      title: "An 3 Schrëtt zum beschten Deal",
      subtitle: "Esou einfach spuerst du mat DealShoppy24",
      steps: [
        { title: "Kucken & Entdecken", description: "Sich duerch eis stänneg aktualiséiert Sammlung vun Aktiounscoden aus allen Kategorien." },
        { title: "Code kréien", description: "Fro däin perséinleche Rabattcode un — direkt per SMS op däin Handy." },
        { title: "Aléisen & Spueren", description: "Benotz de Code beim Online-Shop vun denger Wiel a genéiss direkt Erspuernisser." },
      ],
    },
    benefits: {
      title: "Firwat DealShoppy24?",
      subtitle: "Méi clever akafen war nach ni esou einfach",
      items: [
        { title: "All Dag nei Coden", description: "Eist Team sicht ronderëm d'Auer no de beschten Offere fir dech." },
        { title: "100% verifizéiert", description: "All Aktiounscode gëtt virun der Verëffentlechung gepréift an getest." },
        { title: "Direkt Liwwerung", description: "Kritt däi Rabattcode bannent Sekonnen direkt op däi Smartphone." },
        { title: "All Top-Marken", description: "Vu lokal Lëtzebuerger Geschäfter bis zu international Markeréisen." },
      ],
    },
    popular: {
      title: "Beléift Kategorien",
      subtitle: "Entdeck Remisen an de meescht gesichte Beräicher",
    },
    faq: {
      title: "Heefeg gestallt Froen",
      items: [
        { q: "Wéi kréien ech mäin Aktiounscode?", a: "Nodeems du dech ugemellt hues, schécke mir dir d'Aktiounscoden direkt per SMS op däi Handy. Esou hues du däi Remisen ëmmer parat." },
        { q: "Sinn d'Aktiounscoden wierklech gratis?", a: "D'Aktiounscoden selwer ginn dir Zougang zu exklusiven Remisen bei Partnergeschäfter. Fir den DealShoppy24-Service gëllen d'Konditiounen déi an den AGB beschriwwe sinn." },
        { q: "Fir wéi eng Geschäfter gëllen d'Coden?", a: "Eis Coden gëllen bei enger Villfalt vun Online-Shoppen a lokale Geschäfter zu Lëtzebuerg an Europa — vu Kleedung iwwer Elektronik bis Reesen." },
        { q: "Wéi dacks ginn nei Coden verëffentlecht?", a: "All Dag! Eist Team schafft ronderëm d'Auer fir déi neist a bescht Offere fir dech ze fannen." },
        { q: "Kann ech de Service jiddereen Ament kündegen?", a: "Jo, natierlech. Du kanns de Service jiddereen Ament per SMS kündegen. Schéck einfach STOPDEAL un 67819." },
      ],
    },
    cta: {
      title: "Prett fir ze spueren?",
      subtitle: "Verpass ni méi en Deal. Huel dir elo däin exklusiven Aktiounscoden.",
      button: "Elo starten",
    },
    footer: {
      privacy: "Dateschutz",
      terms: "AGB",
      contact: "Kontakt",
    },
  },
  en: {
    nav: {
      deals: "Deals",
      howItWorks: "How It Works",
      categories: "Categories",
      faq: "FAQ",
    },
    hero: {
      badge: "New in Luxembourg",
      title: "Save up to 70% with exclusive discount codes",
      subtitle: "DealShoppy24 delivers the best promo codes and special offers straight to your phone every day. Fashion, electronics, travel and more — all in one place.",
      cta: "Discover deals",
      trust: "Already thousands of users in Luxembourg",
    },
    deals: {
      title: "Today's Top Deals",
      subtitle: "New promo codes every day — hand-picked and ready to use",
      categories: ["Fashion & Accessories", "Electronics & Tech", "Food & Drinks", "Travel & Leisure", "Home & Garden", "Beauty & Care"],
      discounts: ["Up to 50% off", "Up to 40% off", "Up to 30% off", "Up to 60% off", "Up to 35% off", "Up to 45% off"],
      cta: "Get code",
    },
    howItWorks: {
      title: "Get the Best Deal in 3 Steps",
      subtitle: "Saving with DealShoppy24 is easy",
      steps: [
        { title: "Browse & Discover", description: "Explore our constantly updated collection of promo codes across all categories." },
        { title: "Get Your Code", description: "Request your personal discount code — sent directly to your phone via SMS." },
        { title: "Redeem & Save", description: "Use the code at your favourite online store and enjoy instant savings." },
      ],
    },
    benefits: {
      title: "Why DealShoppy24?",
      subtitle: "Smart shopping has never been easier",
      items: [
        { title: "Fresh codes daily", description: "Our team searches around the clock for the best deals for you." },
        { title: "100% verified", description: "Every promo code is checked and tested before it goes live." },
        { title: "Instant delivery", description: "Receive your discount code within seconds directly on your smartphone." },
        { title: "All top brands", description: "From local Luxembourg shops to international brand giants." },
      ],
    },
    popular: {
      title: "Popular Categories",
      subtitle: "Discover discounts in the most sought-after areas",
    },
    faq: {
      title: "Frequently Asked Questions",
      items: [
        { q: "How do I receive my promo code?", a: "After registering, we send you the promo codes directly via SMS to your mobile phone. Your discounts are always at hand." },
        { q: "Are the promo codes really free?", a: "The promo codes give you access to exclusive discounts at partner stores. The DealShoppy24 service conditions are described in the terms and conditions." },
        { q: "Which stores do the codes work for?", a: "Our codes work at a wide variety of online shops and local stores in Luxembourg and across Europe — from fashion to electronics to travel." },
        { q: "How often are new codes published?", a: "Every day! Our team works around the clock to find the latest and best offers for you." },
        { q: "Can I cancel the service at any time?", a: "Yes, of course. You can cancel the service at any time via SMS. Simply send STOPDEAL to 67819." },
      ],
    },
    cta: {
      title: "Ready to Save?",
      subtitle: "Never miss a deal again. Get your exclusive promo codes now.",
      button: "Get started",
    },
    footer: {
      privacy: "Privacy",
      terms: "Terms",
      contact: "Contact",
    },
  },
};

const categoryIcons = [ShoppingBag, Smartphone, Gift, Star, Search, Zap];
const categoryColors = [
  "from-rose-500 to-pink-600",
  "from-blue-500 to-indigo-600",
  "from-amber-500 to-orange-600",
  "from-emerald-500 to-teal-600",
  "from-violet-500 to-purple-600",
  "from-cyan-500 to-sky-600",
];

function App() {
  const [lang, setLang] = useState('de');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const dropdownRef = useRef(null);

  const t = content[lang];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setLangDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const currentLang = LANGUAGES.find(l => l.code === lang);

  return (
    <div className="min-h-screen bg-white font-body text-dark">

      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <a href="/" className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Tag className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading font-bold text-xl text-dark">
                Deal<span className="text-primary">Shoppy</span>24
              </span>
            </a>

            <nav className="hidden md:flex items-center space-x-8">
              <a href="#deals" className="text-dark/80 hover:text-primary transition-colors font-medium text-sm">{t.nav.deals}</a>
              <a href="#how-it-works" className="text-dark/80 hover:text-primary transition-colors font-medium text-sm">{t.nav.howItWorks}</a>
              <a href="#categories" className="text-dark/80 hover:text-primary transition-colors font-medium text-sm">{t.nav.categories}</a>
              <a href="#faq" className="text-dark/80 hover:text-primary transition-colors font-medium text-sm">{t.nav.faq}</a>
            </nav>

            <div className="flex items-center space-x-3">
              {/* Language Dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setLangDropdownOpen(!langDropdownOpen)}
                  className="flex items-center space-x-2 px-3 py-2 rounded-lg border border-gray-200 hover:border-primary/40 transition-colors text-sm"
                >
                  <ReactCountryFlag countryCode={currentLang.flag} svg style={{ width: '18px', height: '14px' }} />
                  <span className="font-medium hidden sm:inline">{currentLang.code.toUpperCase()}</span>
                  <ChevronDown className="w-3.5 h-3.5 text-muted" />
                </button>
                {langDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 py-1 z-50">
                    {LANGUAGES.map((l) => (
                      <button
                        key={l.code}
                        onClick={() => { setLang(l.code); setLangDropdownOpen(false); }}
                        className={`w-full flex items-center space-x-3 px-4 py-2.5 hover:bg-light transition-colors text-sm ${
                          lang === l.code ? 'text-primary font-semibold bg-primary/5' : 'text-dark'
                        }`}
                      >
                        <ReactCountryFlag countryCode={l.flag} svg style={{ width: '20px', height: '15px' }} />
                        <span>{l.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <a href="/s" className="hidden sm:inline-flex items-center bg-primary text-white px-5 py-2 rounded-lg hover:bg-primary-dark transition-colors font-semibold text-sm shadow-sm">
                {t.hero.cta}
              </a>

              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 rounded-lg text-dark hover:bg-gray-100 transition-colors"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
            <div className="px-4 py-3 space-y-1">
              {[
                { href: "#deals", label: t.nav.deals },
                { href: "#how-it-works", label: t.nav.howItWorks },
                { href: "#categories", label: t.nav.categories },
                { href: "#faq", label: t.nav.faq },
              ].map(link => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-3 py-2.5 text-dark hover:text-primary transition-colors font-medium rounded-lg hover:bg-light"
                >
                  {link.label}
                </a>
              ))}
              <a
                href="/s"
                className="block text-center bg-primary text-white px-4 py-3 rounded-lg font-semibold mt-2"
              >
                {t.hero.cta}
              </a>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="pt-24 pb-20 md:pt-32 md:pb-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-rose-50 via-amber-50/40 to-white" />
        <div className="absolute top-20 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-secondary/5 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-1.5 rounded-full text-sm font-semibold mb-6">
                <Zap className="w-4 h-4" />
                <span>{t.hero.badge}</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-dark mb-6 font-heading leading-tight">
                {t.hero.title}
              </h1>
              <p className="text-lg text-muted mb-8 leading-relaxed max-w-xl">
                {t.hero.subtitle}
              </p>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <a
                  href="/s"
                  className="inline-flex items-center bg-primary text-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-primary-dark transition-all shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30"
                >
                  {t.hero.cta}
                  <ChevronRight className="ml-2 w-5 h-5" />
                </a>
                <div className="flex items-center space-x-2 text-sm text-muted">
                  <div className="flex -space-x-2">
                    {['🇱🇺', '🇩🇪', '🇫🇷', '🇧🇪'].map((flag, i) => (
                      <span key={i} className="w-7 h-7 rounded-full bg-light border-2 border-white flex items-center justify-center text-sm">{flag}</span>
                    ))}
                  </div>
                  <span>{t.hero.trust}</span>
                </div>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <img
                src="/images/hero-deals.jpg"
                alt="DealShoppy24 discount codes and savings"
                className="rounded-2xl shadow-2xl w-full"
              />
              <div className="absolute -bottom-4 -left-4 bg-white p-4 rounded-xl shadow-lg border border-gray-100">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-bold text-dark">-70%</p>
                    <p className="text-xs text-muted">Top Deal</p>
                  </div>
                </div>
              </div>
              <div className="absolute -top-4 -right-4 bg-white p-4 rounded-xl shadow-lg border border-gray-100">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                    <Tag className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-bold text-dark">1000+</p>
                    <p className="text-xs text-muted">Codes</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Deals / Categories Grid */}
      <section id="deals" className="py-20 bg-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4 font-heading">{t.deals.title}</h2>
            <p className="text-muted text-lg max-w-2xl mx-auto">{t.deals.subtitle}</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {t.deals.categories.map((category, i) => {
              const Icon = categoryIcons[i];
              return (
                <a
                  key={i}
                  href="/s"
                  className="group relative bg-white rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-primary/20 overflow-hidden"
                >
                  <div className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-br ${categoryColors[i]} opacity-10 rounded-bl-full group-hover:opacity-20 transition-opacity`} />
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${categoryColors[i]} flex items-center justify-center mb-4`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-heading font-semibold text-dark text-lg mb-1">{category}</h3>
                  <p className="text-primary font-semibold text-sm mb-4">{t.deals.discounts[i]}</p>
                  <span className="inline-flex items-center text-sm font-medium text-primary group-hover:translate-x-1 transition-transform">
                    {t.deals.cta} <ChevronRight className="w-4 h-4 ml-1" />
                  </span>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4 font-heading">{t.howItWorks.title}</h2>
            <p className="text-muted text-lg max-w-2xl mx-auto">{t.howItWorks.subtitle}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 relative">
            <div className="hidden md:block absolute top-16 left-1/6 right-1/6 h-0.5 bg-gradient-to-r from-primary/20 via-primary/40 to-primary/20" />
            {t.howItWorks.steps.map((step, i) => {
              const icons = [Search, Smartphone, Percent];
              const Icon = icons[i];
              return (
                <div key={i} className="text-center relative">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-primary text-white rounded-2xl mb-6 text-xl font-bold shadow-lg shadow-primary/20 relative z-10">
                    {i + 1}
                  </div>
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-primary/10 text-primary rounded-xl mb-4">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-semibold text-dark mb-3 font-heading">{step.title}</h3>
                  <p className="text-muted leading-relaxed">{step.description}</p>
                </div>
              );
            })}
          </div>
          <div className="text-center mt-12">
            <a
              href="/s"
              className="inline-flex items-center bg-primary text-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-primary-dark transition-all shadow-lg shadow-primary/25"
            >
              {t.hero.cta}
              <ChevronRight className="ml-2 w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* Image Feature Section */}
      <section className="py-20 bg-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img
                src="/images/deals-phone.jpg"
                alt="DealShoppy24 on smartphone"
                className="rounded-2xl shadow-2xl w-full"
              />
              <div className="absolute -bottom-4 -right-4 bg-secondary text-white p-4 rounded-xl shadow-lg">
                <p className="font-bold text-2xl">24/7</p>
                <p className="text-sm text-white/80">{lang === 'de' ? 'Neue Deals' : lang === 'fr' ? 'Nouvelles offres' : lang === 'lb' ? 'Nei Offeren' : 'New Deals'}</p>
              </div>
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-dark mb-6 font-heading">{t.benefits.title}</h2>
              <p className="text-lg text-muted mb-8">{t.benefits.subtitle}</p>
              <div className="space-y-6">
                {t.benefits.items.map((item, i) => {
                  const icons = [Clock, ShieldCheck, Zap, Globe];
                  const Icon = icons[i];
                  return (
                    <div key={i} className="flex items-start space-x-4">
                      <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Icon className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-dark mb-1 font-heading">{item.title}</h3>
                        <p className="text-muted text-sm leading-relaxed">{item.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-8">
                <a
                  href="/s"
                  className="inline-flex items-center bg-primary text-white px-6 py-3 rounded-xl font-semibold hover:bg-primary-dark transition-all shadow-sm"
                >
                  {t.cta.button}
                  <ChevronRight className="ml-2 w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Popular Categories Banner */}
      <section id="categories" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4 font-heading">{t.popular.title}</h2>
            <p className="text-muted text-lg max-w-2xl mx-auto">{t.popular.subtitle}</p>
          </div>
          <div className="relative rounded-2xl overflow-hidden">
            <img
              src="/images/shopping-categories.jpg"
              alt="Shopping categories"
              className="w-full h-64 md:h-80 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-dark/80 via-dark/50 to-transparent" />
            <div className="absolute inset-0 flex items-center">
              <div className="px-8 md:px-12 max-w-lg">
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-4 font-heading">
                  {lang === 'de' ? '1000+ aktive Gutscheincodes' : lang === 'fr' ? '1000+ codes promo actifs' : lang === 'lb' ? '1000+ aktiv Aktiounscoden' : '1000+ active promo codes'}
                </h3>
                <p className="text-white/80 mb-6">
                  {lang === 'de' ? 'Durchstöbere die größte Sammlung verifizierter Rabattcodes in Luxemburg.' : lang === 'fr' ? 'Parcourez la plus grande collection de codes de réduction vérifiés au Luxembourg.' : lang === 'lb' ? 'Sich duerch déi gréisst Sammlung vu verifizéierten Rabattcoden zu Lëtzebuerg.' : 'Browse the largest collection of verified discount codes in Luxembourg.'}
                </p>
                <a
                  href="/s"
                  className="inline-flex items-center bg-white text-dark px-6 py-3 rounded-xl font-semibold hover:bg-gray-100 transition-colors shadow-lg"
                >
                  {t.hero.cta}
                  <ChevronRight className="ml-2 w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 bg-light">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4 font-heading">{t.faq.title}</h2>
          </div>
          <div className="space-y-3">
            {t.faq.items.map((item, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-5 text-left"
                >
                  <span className="font-semibold text-dark pr-4">{item.q}</span>
                  <ChevronDown className={`w-5 h-5 text-muted flex-shrink-0 transition-transform duration-200 ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5">
                    <p className="text-muted leading-relaxed">{item.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-primary to-primary-dark relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-40 h-40 border-2 border-white rounded-full" />
          <div className="absolute bottom-10 right-10 w-60 h-60 border-2 border-white rounded-full" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 border border-white rounded-full" />
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <Gift className="w-12 h-12 text-white/80 mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 font-heading">{t.cta.title}</h2>
          <p className="text-white/80 text-lg mb-8 max-w-xl mx-auto">{t.cta.subtitle}</p>
          <a
            href="/s"
            className="inline-flex items-center bg-white text-primary px-8 py-4 rounded-xl text-lg font-semibold hover:bg-gray-50 transition-all shadow-lg hover:shadow-xl"
          >
            {t.cta.button}
            <ChevronRight className="ml-2 w-5 h-5" />
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-dark text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex justify-center items-center space-x-2 mb-6">
              <div className="w-7 h-7 bg-primary rounded-lg flex items-center justify-center">
                <Tag className="w-4 h-4 text-white" />
              </div>
              <span className="font-heading font-bold text-lg">
                Deal<span className="text-primary">Shoppy</span>24
              </span>
            </div>
            <div className="flex justify-center space-x-6 mb-6">
              <a href="/privacy.html" className="text-gray-400 hover:text-white transition-colors text-sm">{t.footer.privacy}</a>
              <span className="text-gray-600">|</span>
              <a href="/terms.html" className="text-gray-400 hover:text-white transition-colors text-sm">{t.footer.terms}</a>
              <span className="text-gray-600">|</span>
              <a href="/contact.html" className="text-gray-400 hover:text-white transition-colors text-sm">{t.footer.contact}</a>
            </div>
            <p className="text-gray-500 text-sm mb-8">
              © {new Date().getFullYear()} DealShoppy24. All rights reserved.
            </p>

            <div className="border-t border-gray-800 pt-8 mt-8">
              <div className="max-w-4xl mx-auto">
                <p className="text-xs text-gray-600 leading-relaxed">
                  *Sie akzeptieren ein Abonnement für unseren Dienst von Clickzillas B.V. Nachdem Sie Ihre Mobiltelefonnummer auf dieser Website eingegeben haben, erhalten Sie eine kostenlose SMS mit Informationen zum Abonnement. Durch das Senden einer SMS akzeptieren Sie die Allgemeinen Geschäftsbedingungen von DealShoppy24 und aktivieren das Abonnement. <a href="/terms.html" className="underline hover:text-gray-400">Klicken Sie hier für die Allgemeinen Geschäftsbedingungen</a>. Der Dienst kostet 3x3,00 €/Woche. Es gelten die Downloadgebühren (WAP, GPRS) Ihres Mobilfunkanbieters. Ihr Telefon muss internetfähig sein. Um sich zu registrieren und/oder den Dienst zu nutzen, müssen Sie der Abonnent/Kontoinhaber sein und mindestens 18 Jahre alt sein oder die ausdrückliche Erlaubnis des Abonnenten/Kontoinhabers haben. Alle Tests, Spiele und/oder Anwendungen auf dieser Seite dienen ausschließlich Unterhaltungszwecken. Eine Kündigung ist jederzeit per SMS möglich. Senden Sie einfach STOPDEAL an 67819 (Standardtarif). E-Mail an help@clickzillas.com. Clickzillas B.V., Jacob van Lennepkade 334Q, 1053 NJ, Amsterdam, Niederlande.
                </p>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
